import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { ContactModal } from './ContactModal';

export const Header = ({ topnavData, settings }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const shopNowUrl = topnavData?.shop_now_url || settings?.amazon_shop_url || 'https://amazon.com';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const openContactModal = () => {
    setIsModalOpen(true);
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-black/95 backdrop-blur-md shadow-2xl border-b-2 border-orange-500/30'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div 
              className="flex items-center cursor-pointer group relative"
              onClick={() => window.location.href = '/'}
            >
              <img
                src={settings?.logo_url || "https://customer-assets.emergentagent.com/job_mtg-sleeves/artifacts/36nq3r5g_Artboard3%402x.png"}
                alt="Planeskeeper Accessories"
                className="h-14 object-contain transition-transform duration-300 group-hover:scale-110 drop-shadow-2xl"
                style={{
                  filter: 'drop-shadow(0 0 10px rgba(255, 87, 34, 0.5))',
                  width: 'auto',
                  maxWidth: isMobile ? '12rem' : 'none'
                }}
              />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <button
                onClick={() => scrollToSection('products')}
                className={`gaming-text font-bold uppercase tracking-wider transition-all duration-300 relative group ${
                  isScrolled
                    ? 'text-gray-300 hover:text-orange-500'
                    : 'text-gray-300 hover:text-orange-400'
                }`}
              >
                Products
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-orange-500 to-red-500 group-hover:w-full transition-all duration-300"></span>
              </button>
              <button
                onClick={() => scrollToSection('features')}
                className={`gaming-text font-bold uppercase tracking-wider transition-all duration-300 relative group ${
                  isScrolled
                    ? 'text-gray-300 hover:text-orange-500'
                    : 'text-gray-300 hover:text-orange-400'
                }`}
              >
                Features
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-orange-500 to-red-500 group-hover:w-full transition-all duration-300"></span>
              </button>
              <button
                onClick={() => window.location.href = '/relics'}
                className={`gaming-text font-bold uppercase tracking-wider transition-all duration-300 relative group ${
                  isScrolled
                    ? 'text-gray-300 hover:text-orange-500'
                    : 'text-gray-300 hover:text-orange-400'
                }`}
              >
                Relic Program
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-orange-500 to-red-500 group-hover:w-full transition-all duration-300"></span>
              </button>
              <button
                onClick={openContactModal}
                className={`gaming-text font-bold uppercase tracking-wider transition-all duration-300 relative group ${
                  isScrolled
                    ? 'text-gray-300 hover:text-orange-500'
                    : 'text-gray-300 hover:text-orange-400'
                }`}
              >
                Contact
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-orange-500 to-red-500 group-hover:w-full transition-all duration-300"></span>
              </button>
              <Button
                onClick={() => window.open(shopNowUrl, '_blank', 'noopener,noreferrer')}
                className="gaming-text bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 text-white font-bold uppercase tracking-wider shadow-lg shadow-orange-600/50 transition-all duration-300 hover:scale-105 border border-orange-500/50"
              >
                Shop Now
              </Button>
            </nav>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`md:hidden p-2 rounded-lg transition-all duration-300 border ${
                isScrolled
                  ? 'hover:bg-orange-600/20 text-orange-400 border-orange-500/30'
                  : 'hover:bg-orange-600/20 text-orange-400 border-orange-500/30'
              }`}
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-black/98 border-t-2 border-orange-500/30 shadow-lg backdrop-blur-md">
            <nav className="px-4 py-4 space-y-3">
              <button
                onClick={() => scrollToSection('products')}
                className="gaming-text block w-full text-left px-4 py-3 text-gray-300 hover:bg-orange-600/20 hover:text-orange-400 rounded-lg transition-all duration-200 font-bold uppercase tracking-wider border border-transparent hover:border-orange-500/30"
              >
                Products
              </button>
              <button
                onClick={() => scrollToSection('features')}
                className="gaming-text block w-full text-left px-4 py-3 text-gray-300 hover:bg-orange-600/20 hover:text-orange-400 rounded-lg transition-all duration-200 font-bold uppercase tracking-wider border border-transparent hover:border-orange-500/30"
              >
                Features
              </button>
              <button
                onClick={() => window.location.href = '/relics'}
                className="gaming-text block w-full text-left px-4 py-3 text-gray-300 hover:bg-orange-600/20 hover:text-orange-400 rounded-lg transition-all duration-200 font-bold uppercase tracking-wider border border-transparent hover:border-orange-500/30"
              >
                Relic Program
              </button>
              <button
                onClick={openContactModal}
                className="gaming-text block w-full text-left px-4 py-3 text-gray-300 hover:bg-orange-600/20 hover:text-orange-400 rounded-lg transition-all duration-200 font-bold uppercase tracking-wider border border-transparent hover:border-orange-500/30"
              >
                Contact
              </button>
              <Button
                onClick={() => window.open(shopNowUrl, '_blank', 'noopener,noreferrer')}
                className="gaming-text w-full bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 text-white font-bold uppercase tracking-wider"
              >
                Shop Now
              </Button>
            </nav>
          </div>
        )}
      </header>

      {/* Contact Modal */}
      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
};